import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { formatSsLabel, resolveUnitOps, resolveUnitTypeDisplayName } from '../utils/unitDisplay';
import {
  parseUnitUniqueId,
  buildUnitUniqueId,
  formatUnitIdShort,
  isSuffixTaken,
  sortSsUnitsByNumber,
  unitMatchesEquipmentType,
} from '../utils/unitIdFormat';

function isInactiveUnit(unit) {
  return String(unit?.status || 'ACTIVE').toUpperCase() === 'INACTIVE';
}

function UnitIdRow({
  unit,
  usedUniqueIds,
  highlighted,
  onHighlight,
  onOpenUnit,
  onOpenInspectionList,
  onSaved,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  reorderEnabled,
  onGripDragStart,
  dragging,
  dragOver,
  onDragOverRow,
  onDropRow,
  onDragEnd,
  equipmentTypes = [],
}) {
  const parsed = parseUnitUniqueId(unit.unique_id, unit.ss_code);
  const typeDisplayName = resolveUnitTypeDisplayName(unit, { equipmentTypes });
  const [suffix, setSuffix] = useState(parsed.suffix || '1');
  const queryClient = useQueryClient();

  useEffect(() => {
    const p = parseUnitUniqueId(unit.unique_id, unit.ss_code);
    setSuffix(p.suffix || '1');
  }, [unit.id, unit.unique_id, unit.ss_code]);

  const taken = isSuffixTaken(parsed.prefix, suffix, usedUniqueIds, unit.unique_id);
  const unchanged =
    String(parseInt(suffix, 10) || '') === String(parseInt(parsed.suffix || '0', 10) || '');
  const inactive = isInactiveUnit(unit);
  const label = formatUnitIdShort(unit.unique_id, unit.ss_code);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const unique_id = buildUnitUniqueId(parsed.prefix, suffix);
      if (!unique_id) throw new Error('Invalid unit number');
      if (isSuffixTaken(parsed.prefix, suffix, usedUniqueIds, unit.unique_id)) {
        throw new Error('This number is already used — choose a free number');
      }
      const oldId = String(unit.unique_id || '').trim();
      const oldName = String(unit.name || '').trim();
      // Keep Name in sync with Unit ID when it still mirrors the old ID (or was empty / type-only)
      const typeLabel = String(typeDisplayName || unit.unit_type || '').trim();
      const syncName =
        !oldName ||
        oldName === oldId ||
        oldName.toLowerCase() === oldId.toLowerCase() ||
        (typeLabel && oldName === typeLabel);
      const body = { unique_id };
      if (syncName) body.name = unique_id;
      const res = await api.put(`/ss-units/${unit.id}`, body);
      return res.data?.data ?? res.data;
    },
    onSuccess: (data) => {
      const next = formatUnitIdShort(
        data?.unique_id || buildUnitUniqueId(parsed.prefix, suffix),
        unit.ss_code
      );
      toast.success(`Unit ID → ${next}`);
      queryClient.invalidateQueries({ queryKey: ['building', unit.building_id, 'ss-units'] });
      onSaved?.(data);
    },
    onError: (e) => {
      const status = e.response?.status;
      const msg =
        e.response?.data?.error ||
        e.message ||
        'Failed to update ID';
      if (status === 409 || /already/i.test(msg)) {
        toast.error(msg);
        // Revert input to saved suffix so we do not keep a duplicate draft
        const p = parseUnitUniqueId(unit.unique_id, unit.ss_code);
        setSuffix(p.suffix || '1');
        return;
      }
      toast.error(msg);
    },
  });

  const handleDeleteActive = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete unit ${label}?\n\nIt becomes inactive until you remove it from the list.`)) {
      return;
    }
    onDelete?.(unit, { skipConfirm: true, hard: false });
  };

  const handleRemoveFromList = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Remove ${label} from the list?\n\nThis frees the ID so it can be reused.`)) {
      return;
    }
    onDelete?.(unit, { skipConfirm: true, hard: true });
  };

  const trySaveSuffix = () => {
    if (inactive || saveMutation.isPending) return;
    if (!suffix) {
      toast.error('Enter a unit number');
      return;
    }
    if (taken) {
      toast.error('This number is already used — choose a free number');
      const p = parseUnitUniqueId(unit.unique_id, unit.ss_code);
      setSuffix(p.suffix || '1');
      return;
    }
    if (unchanged) return;
    saveMutation.mutate();
  };

  return (
    <li
      onDragOver={(e) => {
        if (!reorderEnabled || inactive) return;
        e.preventDefault();
        onDragOverRow?.(e, unit);
      }}
      onDrop={(e) => {
        if (!reorderEnabled || inactive) return;
        e.preventDefault();
        onDropRow?.(e, unit);
      }}
      onDragEnd={onDragEnd}
      className={`border rounded-lg p-2.5 transition-colors ${
        highlighted ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-card'
      } ${inactive ? 'opacity-80' : ''} ${dragging ? 'opacity-40' : ''} ${
        dragOver ? 'ring-2 ring-primary-400 border-primary-400' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1">
            {reorderEnabled && !inactive ? (
              <button
                type="button"
                draggable
                onDragStart={(e) => onGripDragStart?.(e, unit)}
                className="mt-0.5 p-0.5 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing shrink-0"
                title="Drag to reorder"
                aria-label="Drag to reorder"
              >
                <GripVertical className="w-4 h-4" />
              </button>
            ) : (
              <span className="w-5 shrink-0" />
            )}
            <button
              type="button"
              className="min-w-0 flex-1 text-left text-[11px] text-gray-500 truncate"
              onClick={() => {
                onHighlight?.(unit.id);
                if (!inactive) onOpenUnit?.(unit);
              }}
              title={inactive ? resolveUnitOps(unit).location || unit.unique_id : 'Open unit details'}
            >
              <span className="font-mono font-semibold text-primary-800">{label}</span>
              {inactive ? ' · inactive' : ''}
            </button>
          </div>
          {!inactive && typeDisplayName ? (
            <button
              type="button"
              className="block w-full text-left pl-5 text-[11px] text-primary-700 hover:underline truncate"
              onClick={(e) => {
                e.stopPropagation();
                onHighlight?.(unit.id);
                if (onOpenInspectionList) onOpenInspectionList(unit);
                else onOpenUnit?.(unit);
              }}
              title="View inspection questions for this type"
            >
              {typeDisplayName}
            </button>
          ) : resolveUnitOps(unit).location ? (
            <span className="block truncate text-gray-500 pl-5 text-[11px]">
              {resolveUnitOps(unit).location}
            </span>
          ) : null}
        </div>
        {!inactive && (
          <div className="flex items-center gap-0.5 shrink-0">
            {reorderEnabled && (
              <>
                <button
                  type="button"
                  disabled={!canMoveUp}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveUp?.(unit);
                  }}
                  className="p-1 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30"
                  title="Move up (renumber)"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  disabled={!canMoveDown}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveDown?.(unit);
                  }}
                  className="p-1 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-30"
                  title="Move down (renumber)"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={handleDeleteActive}
              className="p-1.5 rounded-md text-red-600 hover:bg-red-50"
              title="Delete unit"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {!inactive && (
        <div className="flex items-stretch gap-1.5 pl-5">
          <div className="flex items-stretch flex-1 min-w-0">
            <span className="inline-flex items-center px-2 py-1.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md text-xs font-mono text-gray-700 shrink-0">
              {parsed.prefix}-
            </span>
            <input
              type="number"
              min={1}
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
              onFocus={() => onHighlight?.(unit.id)}
              onBlur={() => trySaveSuffix()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  trySaveSuffix();
                }
              }}
              className={`w-full min-w-0 px-2 py-1.5 border rounded-r-md text-sm font-mono ${
                taken ? 'border-red-400 bg-red-50' : 'border-gray-300'
              }`}
              aria-label={`Unit number for ${label}`}
              aria-invalid={taken}
            />
          </div>
        </div>
      )}
      {taken && !inactive ? (
        <p className="text-[11px] text-red-600 mt-1 pl-5 font-medium">
          Already used — pick another number (change not saved)
        </p>
      ) : null}
      {inactive ? (
        <div className="pl-5 mt-1">
          <button
            type="button"
            onClick={handleRemoveFromList}
            className="inline-flex items-center gap-1.5 w-full justify-center px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Remove from list
          </button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Right-side Floor Plan panel: edit / delete / reorder SS unit IDs.
 */
export default function FloorPlanChangeUnitIdPanel({
  buildingId,
  unitsAll = [],
  ssInstances = [],
  selectedInstanceId,
  onSelectInstance,
  selectedEquipmentTypeId,
  showAllSubs = false,
  highlightUnitId,
  onHighlightUnit,
  onDeleteUnit,
  onOpenUnit,
  onOpenInspectionList,
  /** When true, SS filter is locked (e.g. SS Units page already chose an SS). */
  lockSsFilter = false,
  className = '',
}) {
  const queryClient = useQueryClient();
  const [localOrder, setLocalOrder] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [renumbering, setRenumbering] = useState(false);
  const dragIdRef = useRef(null);

  const usedUniqueIds = useMemo(
    () =>
      unitsAll
        .filter((u) => {
          const st = String(u?.status || 'ACTIVE').toUpperCase();
          if (st === 'INACTIVE' || st === 'PURGED') return false;
          const id = String(u?.unique_id || '');
          return id && !id.startsWith('__');
        })
        .map((u) => u.unique_id),
    [unitsAll]
  );
  const selectedInst = ssInstances.find((i) => String(i.id) === String(selectedInstanceId));

  const sortedSource = useMemo(() => {
    let rows = unitsAll.filter((u) => String(u.status || '').toUpperCase() !== 'PURGED');
    if (selectedInstanceId) {
      rows = rows.filter(
        (u) => String(u.building_ss_instance_id) === String(selectedInstanceId)
      );
    }
    const types = selectedInst?.equipment_types || [];
    if (types.length && !showAllSubs) {
      const type = types.find((t) => String(t.id) === String(selectedEquipmentTypeId));
      if (type) rows = rows.filter((u) => unitMatchesEquipmentType(u, type));
    }
    return sortSsUnitsByNumber(rows);
  }, [unitsAll, selectedInstanceId, selectedInst, showAllSubs, selectedEquipmentTypeId]);

  useEffect(() => {
    setLocalOrder(null);
  }, [selectedInstanceId, selectedEquipmentTypeId, showAllSubs, unitsAll]);

  const list = useMemo(() => {
    if (!localOrder) return sortedSource;
    const byId = new Map(sortedSource.map((u) => [String(u.id), u]));
    const ordered = localOrder.map((id) => byId.get(String(id))).filter(Boolean);
    const missing = sortedSource.filter((u) => !localOrder.includes(String(u.id)));
    return [...ordered, ...missing];
  }, [localOrder, sortedSource]);

  const activeList = useMemo(() => list.filter((u) => !isInactiveUnit(u)), [list]);
  const inactiveList = useMemo(() => list.filter((u) => isInactiveUnit(u)), [list]);

  const reorderEnabled = !!selectedInstanceId && activeList.length > 1;

  const applyRenumber = useCallback(
    async (orderedActive) => {
      if (!selectedInstanceId || orderedActive.length < 1) return;
      const bId = buildingId || orderedActive[0]?.building_id;
      if (!bId) {
        toast.error('Building id missing ??cannot renumber');
        return;
      }
      setRenumbering(true);
      try {
        await api.post(`/buildings/${bId}/ss-instances/${selectedInstanceId}/units/reorder`, {
          unitIds: orderedActive.map((u) => u.id),
        });
        await queryClient.invalidateQueries({ queryKey: ['building', bId, 'ss-units'] });
        setLocalOrder(null);
        toast.success(`IDs updated to 1??{orderedActive.length}`);
      } catch (e) {
        console.error('[reorder]', e);
        toast.error(e.response?.data?.error || e.message || 'Failed to renumber');
        setLocalOrder(null);
      } finally {
        setRenumbering(false);
      }
    },
    [selectedInstanceId, buildingId, queryClient]
  );

  const reorderTo = useCallback(
    async (fromId, toId) => {
      if (!reorderEnabled) return;
      const actives = [...activeList];
      const fromIdx = actives.findIndex((u) => String(u.id) === String(fromId));
      const toIdx = actives.findIndex((u) => String(u.id) === String(toId));
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;

      const next = [...actives];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);

      setLocalOrder([...next.map((u) => String(u.id)), ...inactiveList.map((u) => String(u.id))]);
      await applyRenumber(next);
    },
    [reorderEnabled, activeList, inactiveList, applyRenumber]
  );

  const moveBy = useCallback(
    async (unit, delta) => {
      const actives = [...activeList];
      const idx = actives.findIndex((u) => String(u.id) === String(unit.id));
      if (idx < 0) return;
      const toIdx = idx + delta;
      if (toIdx < 0 || toIdx >= actives.length) return;
      await reorderTo(unit.id, actives[toIdx].id);
    },
    [activeList, reorderTo]
  );

  const onGripDragStart = (e, unit) => {
    if (!reorderEnabled || isInactiveUnit(unit)) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    dragIdRef.current = String(unit.id);
    setDragId(String(unit.id));
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(unit.id));
  };

  const onDragOverRow = (e, unit) => {
    if (!dragIdRef.current || isInactiveUnit(unit)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (String(unit.id) !== overId) setOverId(String(unit.id));
  };

  const onDropRow = async (e, target) => {
    e.preventDefault();
    const fromId = dragIdRef.current || e.dataTransfer.getData('text/plain');
    setOverId(null);
    setDragId(null);
    dragIdRef.current = null;
    if (!fromId || String(target.id) === String(fromId)) return;
    await reorderTo(fromId, target.id);
  };

  const onDragEnd = () => {
    setDragId(null);
    setOverId(null);
    dragIdRef.current = null;
  };

  return (
    <aside
      className={`flex flex-col h-full min-h-0 w-full shrink-0 border border-gray-200 rounded-lg bg-card overflow-hidden ${className}`}
    >
      <div className="shrink-0 px-3 py-2.5 border-b bg-slate-50">
        <h2 className="text-sm font-semibold text-gray-900">Change SS unit ID</h2>
        <p className="text-[11px] text-gray-500 mt-0.5">
          Edit the number (saved when you leave the field), use ?�↓ or drag to renumber, delete active ??inactive, then{' '}
          <strong>Remove from list</strong> for inactive.
        </p>
        {renumbering ? (
          <p className="text-[11px] text-primary-700 mt-1 font-medium">Updating IDs…</p>
        ) : null}
      </div>

      {!lockSsFilter ? (
        <div className="shrink-0 px-3 py-2 border-b">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">SS filter</label>
          <select
            value={
              selectedInstanceId != null && selectedInstanceId !== ''
                ? String(selectedInstanceId)
                : ''
            }
            onChange={(e) => onSelectInstance?.(e.target.value || null)}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs bg-white"
          >
            <option value="">All SS</option>
            {ssInstances.map((inst) => (
              <option key={inst.id} value={String(inst.id)}>
                {formatSsLabel(inst.ss_code)}
                {inst.location ? ` ??${inst.location}` : ''}
              </option>
            ))}
          </select>
          {!selectedInstanceId ? (
            <p className="text-[11px] text-amber-700 mt-1">Pick one SS to enable reorder (?�↓ / drag).</p>
          ) : activeList.length < 2 ? (
            <p className="text-[11px] text-gray-500 mt-1">Need 2+ active units to reorder.</p>
          ) : (
            <p className="text-[11px] text-emerald-700 mt-1">Reorder enabled ?? use ?�↓ or drag grip.</p>
          )}
        </div>
      ) : (
        <div className="shrink-0 px-3 py-2 border-b bg-card">
          {selectedInst ? (
            <p className="text-[11px] font-semibold text-primary-800">
              {formatSsLabel(selectedInst.ss_code)}
              {selectedInst.location ? ` ??${selectedInst.location}` : ''}
            </p>
          ) : (
            <p className="text-[11px] text-amber-700">Select an SS on the drawing.</p>
          )}
          {activeList.length < 2 ? (
            <p className="text-[11px] text-gray-500 mt-1">Need 2+ active units to reorder with ?�↓ / drag.</p>
          ) : (
            <p className="text-[11px] text-emerald-700 mt-1">Reorder enabled ?? use ?�↓ or drag grip.</p>
          )}
        </div>
      )}

      <ul className="flex-1 min-h-0 overflow-y-auto p-2 space-y-2">
        {list.length === 0 ? (
          <li className="text-sm text-gray-500 p-3 text-center">
            No units yet. Place units on the drawing, or add them under SS Units.
          </li>
        ) : (
          <>
            {activeList.map((unit, idx) => (
              <UnitIdRow
                key={unit.id}
                unit={unit}
                equipmentTypes={selectedInst?.equipment_types || []}
                usedUniqueIds={usedUniqueIds}
                highlighted={String(highlightUnitId) === String(unit.id)}
                onHighlight={onHighlightUnit}
                onOpenUnit={onOpenUnit}
                onOpenInspectionList={onOpenInspectionList}
                onSaved={() => onHighlightUnit?.(unit.id)}
                onDelete={onDeleteUnit}
                onMoveUp={() => moveBy(unit, -1)}
                onMoveDown={() => moveBy(unit, 1)}
                canMoveUp={idx > 0}
                canMoveDown={idx < activeList.length - 1}
                reorderEnabled={reorderEnabled}
                onGripDragStart={onGripDragStart}
                onDragOverRow={onDragOverRow}
                onDropRow={onDropRow}
                onDragEnd={onDragEnd}
                dragging={String(dragId) === String(unit.id)}
                dragOver={String(overId) === String(unit.id) && String(dragId) !== String(unit.id)}
              />
            ))}
            {inactiveList.length > 0 ? (
              <li className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide pt-2 px-1">
                Inactive ??remove from list
              </li>
            ) : null}
            {inactiveList.map((unit) => (
              <UnitIdRow
                key={unit.id}
                unit={unit}
                equipmentTypes={selectedInst?.equipment_types || []}
                usedUniqueIds={usedUniqueIds}
                highlighted={String(highlightUnitId) === String(unit.id)}
                onHighlight={onHighlightUnit}
                onOpenUnit={onOpenUnit}
                onOpenInspectionList={onOpenInspectionList}
                onSaved={() => onHighlightUnit?.(unit.id)}
                onDelete={onDeleteUnit}
                reorderEnabled={false}
              />
            ))}
          </>
        )}
      </ul>
    </aside>
  );
}
